import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import type { Site, EditFormData } from './types';
import { INITIAL_EDIT_FORM } from './types';

export function useSitesManagement() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading, logout } = useAuth();

  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);
  const [editFormData, setEditFormData] = useState<EditFormData>(INITIAL_EDIT_FORM);

  const loadSites = useCallback(async () => {
    try {
      setIsLoading(true);
      const sitesData = await adminApi.getAllSites();
      setSites(sitesData);
    } catch (error) {
      console.error('Failed to load sites:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard');
      return;
    }

    if (isAuthenticated && isAdmin) {
      loadSites();
    }
  }, [navigate, isAuthenticated, isAdmin, authLoading, loadSites]);

  const startEdit = useCallback((site: Site) => {
    setEditingSite(site);
    setEditFormData({
      name: site.name,
      subdomain: site.subdomain,
      customDomain: site.customDomain || '',
    });
    setShowEditModal(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingSite(null);
    setShowEditModal(false);
    setEditFormData(INITIAL_EDIT_FORM);
  }, []);

  const handleUpdateSite = useCallback(async () => {
    if (!editingSite) return;

    try {
      setIsSubmitting(true);
      await adminApi.updateSite(editingSite.id, {
        name: editFormData.name,
        subdomain: editFormData.subdomain,
        customDomain: editFormData.customDomain || null,
      });
      await loadSites();
      cancelEdit();
    } catch (error: any) {
      console.error('Failed to update site:', error);
      alert(error.message || 'Failed to update site');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingSite, editFormData, loadSites, cancelEdit]);

  const startDelete = useCallback((site: Site) => {
    setSiteToDelete(site);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteSite = useCallback(async () => {
    if (!siteToDelete) return;

    try {
      setIsSubmitting(true);
      await adminApi.deleteSite(siteToDelete.id);
      await loadSites();
      setShowDeleteModal(false);
      setSiteToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete site:', error);
      alert(error.message || 'Failed to delete site');
    } finally {
      setIsSubmitting(false);
    }
  }, [siteToDelete, loadSites]);

  const handleToggleLandingPage = useCallback(
    async (site: Site, isLandingPage: boolean) => {
      try {
        setIsSubmitting(true);
        const response = await adminApi.setLandingPage(site.id, isLandingPage);
        await loadSites();
        // Sync the open Edit modal — without this, the controlled checkbox
        // reads from stale editingSite state and visually reverts.
        const newIsLandingPage = !!response?.data?.isLandingPage;
        setEditingSite(prev =>
          prev && prev.id === site.id ? { ...prev, isLandingPage: newIsLandingPage } : prev
        );
      } catch (error: any) {
        console.error('Failed to update landing page designation:', error);
        alert(error?.response?.data?.message || error.message || 'Failed to update landing page');
      } finally {
        setIsSubmitting(false);
      }
    },
    [loadSites]
  );

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setSiteToDelete(null);
  }, []);

  const filteredSites = sites.filter(site => {
    const searchLower = searchTerm.toLowerCase();
    return (
      site.name.toLowerCase().includes(searchLower) ||
      site.subdomain.toLowerCase().includes(searchLower) ||
      (site.customDomain && site.customDomain.toLowerCase().includes(searchLower)) ||
      site.userName.toLowerCase().includes(searchLower) ||
      site.userEmail.toLowerCase().includes(searchLower)
    );
  });

  return {
    // Auth state
    authLoading,
    isAuthenticated,
    isAdmin,
    logout,
    // Sites data
    sites,
    filteredSites,
    isLoading,
    // Search
    searchTerm,
    setSearchTerm,
    // Edit modal
    editingSite,
    showEditModal,
    editFormData,
    setEditFormData,
    startEdit,
    cancelEdit,
    handleUpdateSite,
    // Delete modal
    siteToDelete,
    showDeleteModal,
    startDelete,
    cancelDelete,
    handleDeleteSite,
    // Landing page designation
    handleToggleLandingPage,
    // Submitting state
    isSubmitting,
  };
}
